import { useContext, useState, useRef, useEffect } from "react";
import Logout from "../Logout.js";
import { MENU_ITEMS } from "../../Constants/menuItemsData";
import { UserContext } from "../../Context";
import GreenEn_Logo from "../../../src/Assets/GreenEn_Logo.png";
import { FaAngleDown } from "react-icons/fa";
import NotificationBell from "../../Pages/Notification/NotificationBell.js";
import { BASE } from "../../routes.js";
import { toProperCase } from "../../Utils/index.js";
import { USER_ROLES } from "../../Constants/Strings.js";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openSubMenu, setOpenSubMenu] = useState(null);
  const { user } = useContext(UserContext);
  const userRole = user?.role;

  const headerRef = useRef(null);
  const HEADER_HEIGHT = 64; // px

  useEffect(() => {
    if (headerRef.current) {
      headerRef.current.scrollIntoView({ behavior: "auto", block: "start" });
    }
  }, []);

  const toggleSubMenu = (menuName) => {
    setOpenSubMenu(openSubMenu === menuName ? null : menuName);
  };

  // Get menu items allowed for current role
  const getMenuItems = () =>
    MENU_ITEMS.filter((item) => !item.roles || item.roles.includes(userRole));

  return (
    <nav
      ref={headerRef}
      className="bg-primary py-2 flex justify-between z-50 relative shadow-lg"
    >
      <div className="container flex flex-nowrap justify-between items-center gap-2 mx-auto relative">
        {/* Logo */}
        <a href={BASE} className="flex items-center pl-1 ml-2 md:pl-2">
          <div className="bg-gray-100 p-[2px] md:p-1 rounded-full">
            <img src={GreenEn_Logo} alt="SolarOps Logo" className="h-6 w-6" />
          </div>
          <span className="self-center text-lg md:text-xl font-semibold whitespace-nowrap text-white ml-1 md:ml-2">
            {process.env.REACT_APP_TITLE}
          </span>
        </a>

        {/* Hamburger + Notification + Logout */}
        <div className="flex items-center gap-2 ml-auto pr-2 flex-nowrap overflow-visible relative shrink-0">
          
          <div className="flex flex-col text-white px-2 py-1 shadow rounded max-w-[120px] sm:max-w-[160px] overflow-hidden">
            <span className="truncate whitespace-nowrap">
            Hi {user.first_name}!
            </span>
            {userRole && (
              <span className="text-white text-xs truncate whitespace-nowrap">
                {user.role === USER_ROLES.HR
                  ? USER_ROLES.HR
                  : toProperCase(user.role)}
              </span>
            )}
          </div>

          <div className="relative z-[60] overflow-visible">
            <NotificationBell />
          </div>

          <button
            type="button"
            className="bg-white inline-flex items-center p-1 mx-1 text-sm text-gray-500 rounded-lg ring-2 ring-gray-200 shadow-sm"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <svg className="w-6 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                clipRule="evenodd"
              />
            </svg>
          </button>

          <div className="hidden md:block">
            <Logout isMenuOpen={false} />
          </div>
        </div>

        {/* Menu overlay */}
        {isMenuOpen && (
          <div
            className="fixed inset-0 z-30 bg-black bg-opacity-20"
            onClick={() => {
              setIsMenuOpen(false);
              setOpenSubMenu(null);
            }}
          />
        )}

        {/* Sliding menu */}
        <div
          className={`fixed top-[${HEADER_HEIGHT}px] right-0 w-3/4 max-w-xs bg-white z-40 transition-transform duration-200 ${
            isMenuOpen ? "translate-x-0" : "translate-x-full"
          }`}
          style={{
            top: `${HEADER_HEIGHT}px`,
            height: `calc(100vh - ${HEADER_HEIGHT}px - 20px)`,
          }}
        >
          <ul className="flex flex-col p-4 font-medium relative h-full overflow-y-auto">
            {getMenuItems().map((item, idx) => (
              <li key={idx} className="relative w-full">
                {item.SUB_MENU?.length > 0 ? (
                  <>
                    <span
                      onClick={() => toggleSubMenu(item.name)}
                      className="flex justify-between items-center py-2 px-4 cursor-pointer border-b border-gray-100 rounded-md text-primary hover:text-secondary"
                    >
                      {item.name}
                      <FaAngleDown
                        className={`ml-2 transition-transform ${
                          openSubMenu === item.name ? "rotate-180" : "rotate-0"
                        }`}
                      />
                    </span>
                    <div
                      className={`pl-4 overflow-auto  ${
                        openSubMenu === item.name ? "max-h-96" : "max-h-0"
                      }`}
                    >
                      {item.SUB_MENU.filter(
                        (sub) => !sub.roles || sub.roles.includes(userRole),
                      ).map((subItem, idx) => (
                        <a
                          key={idx}
                          href={subItem.route}
                          className="block py-2 px-4 text-primary bg-white border-b border-gray-100 hover:text-secondary rounded-md"
                        >
                          {subItem.name}
                        </a>
                      ))}
                    </div>
                  </>
                ) : (
                  <a
                    href={item.route}
                    className="block py-2 px-4 border-b border-gray-100 rounded-md text-primary hover:text-secondary"
                  >
                    {item.name}
                  </a>
                )}
              </li>
            ))}

            {/* Logout */}
            <li className="block md:hidden py-2 pr-4 pl-3">
              <Logout isMenuOpen={isMenuOpen} />
            </li>

            {/* Bottom space */}
            <li className="h-5" />
          </ul>
        </div>
      </div>
    </nav>
  );
};

export default Header;
